import React from 'react';
import SkeletonBlock from '../ui/SkeletonBlock';
import SkeletonRoot from '../ui/SkeletonRoot';

interface AdminClientDetailSkeletonProps {
  loadingLabel?: string;
}

/**
 * Workspace-style client detail skeleton — breadcrumb, hero, tabs, panels.
 */
const AdminClientDetailSkeleton: React.FC<AdminClientDetailSkeletonProps> = ({
  loadingLabel = 'Loading client profile',
}) => (
  <SkeletonRoot loadingLabel={loadingLabel}>
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <SkeletonBlock className="h-4 w-20 rounded" />
          <SkeletonBlock className="h-4 w-4 rounded" />
          <SkeletonBlock className="h-4 w-32 rounded" />
        </div>

        <div className="mb-6 rounded-2xl border border-primary-500/10 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <SkeletonBlock className="h-16 w-16 rounded-2xl" />
              <div className="space-y-2">
                <SkeletonBlock className="h-7 w-64 rounded-lg" />
                <SkeletonBlock className="h-4 w-40 rounded" />
              </div>
            </div>
            <SkeletonBlock className="h-9 w-48 rounded-lg" />
          </div>
        </div>

        <div className="mb-6 flex gap-2 overflow-hidden rounded-2xl border border-primary-500/10 bg-white p-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonBlock key={i} className="h-9 w-28 rounded-xl" />
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-4">
            <SkeletonBlock className="h-48 w-full rounded-2xl" />
            <SkeletonBlock className="h-40 w-full rounded-2xl" />
          </div>
          <div className="space-y-6 lg:col-span-8">
            <SkeletonBlock className="h-64 w-full rounded-2xl" />
            <SkeletonBlock className="h-48 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    </div>
  </SkeletonRoot>
);

export default AdminClientDetailSkeleton;
